import { checkAndRedirectToLaunchpad, checkAndRedirectToSummerOfAI } from '../../util/helper'

import NotificationHelper from '../../util/notification_helper'
import { desktopController } from '../../util/DesktopController'
import firebase from 'firebase/compat/app'
import { handleFireStoreError } from '../../plugins/sentry'
import { i18n } from '@/localization'

export async function handleSuccessfulLogin(response: any, store: any, router: any) {
  await firebase.auth().signInWithCustomToken(response.token)

  await store.dispatch('auth/set', {
    firebaseToken: response.token,
    apiKey: response.apiKey,
    userId: response.userId,
    companyId: response.companyId,
    jwt: response.jwt,
    refreshJwt: response.refreshJwt,
    authToken: response.authToken,
    refreshToken: response.refreshToken,
  })

  NotificationHelper.requestPermission()
  await store.dispatch('init')

  try {
    // Handle desktop authentication if needed
    const isDesktopHandled = await desktopController.handleDesktopAuthentication({
      onFallback: async () => {
        await handleRedirect(router, store)
      },
    })
    if (isDesktopHandled) {
      return
    }
  } catch (error) {
    console.error('Error while processing desktop auth in handleSuccessfulLogin', error)
  }

  await handleRedirect(router, store)
}

export async function handleRedirect(router: any, store: any) {
  if (window?.location?.search?.includes('src=marketplace')) {
    window.close()
    return
  }

  const redirect = router.currentRoute.value.query.redirect
  const isCannyLogin =
    router.currentRoute.value.query.app === 'canny' &&
    router.currentRoute.value.query.companyID &&
    router.currentRoute.value.query.redirect

  if (redirect && !isCannyLogin) {
    window.location.replace(redirect as string)
    return
  }

  const url = router.currentRoute.value.query.url
  if (url) {
    router.push({ path: decodeURIComponent(url), replace: true })
  } else {
    try {
      // Redirection priority: launchpad > summer of ai > dashboard
      const redirectedToLaunchpad = await checkAndRedirectToLaunchpad({
        store,
        router,
      })

      if (redirectedToLaunchpad) return

      const redirectedToSummerOfAIMenu = await checkAndRedirectToSummerOfAI({
        store,
        router,
        locationId: '',
      })
      if (!redirectedToSummerOfAIMenu) {
        router.push({ name: 'dashboard', replace: true })
      }
    } catch (err) {
      console.error('redirect to summer of ai failed: ', err)
      router.push({ name: 'dashboard', replace: true })
    }
  }
}

export function handleLoginError(
  error: any,
  email: string
): { errorMessage: string; errorType: string; maxAttemptsReached: boolean } {
  let errorMessage = ''
  let errorType = ''
  let maxAttemptsReached = false
  if (error.response) {
    if (error.response?.data?.statusCode === 429) {
      errorMessage = i18n.global.t('login.exceededLimit')
      maxAttemptsReached = true
      errorType = 'too-many-requests'
    } else {
      errorType = 'api-error'
      errorMessage =
        typeof error.response?.data?.message === 'object'
          ? error.response?.data?.message?.message
          : error.response?.data?.message
    }

    return { errorMessage, errorType, maxAttemptsReached }
  } else if (error.name === 'FirestoreError' || error.name === 'FirebaseError') {
    const redirectDelay = 10000 // 10 seconds delay for redirection

    // Handle specific error cases
    if (
      error.name === 'FirestoreError' ||
      (error.name === 'FirebaseError' && error.code === 'auth/network-request-failed')
    ) {
      handleFireStoreError(error, { email })
      errorMessage = i18n.global.t('login.unknownError')
      errorType = 'firebase-connection-error'
    } else if (error.name === 'FirebaseError' && error.code === 'messaging/permission-blocked') {
      errorMessage = ''
      errorType = 'notification-permission-blocked'
    } else if (error.name === 'FirebaseError') {
      errorMessage = ''
      errorType = 'unknown-firebase-error'
    }

    // Set up automatic redirection
    setTimeout(() => {
      // Do not change the logic for SSO, let it have login=failed in url
      window.location.reload()
    }, redirectDelay)

    return {
      errorMessage: errorMessage,
      errorType: errorType,
      maxAttemptsReached: maxAttemptsReached,
    }
  } else {
    return {
      errorMessage:
        'We encountered an issue while trying to log you in. Please try again later or contact support if the problem persists.',
      errorType: 'unknown-error',
      maxAttemptsReached: maxAttemptsReached,
    }
  }
}
